import debounce from 'lodash/debounce.js';
import { computed, onMounted, reactive, watch } from 'vue';

import axios from '@/plugins/axios.js';

/**
 * Composable for managing filter state and common operations
 * Centralizes repetitive logic used across different filter types
 */
/** Filter types where the dropdown should stay open (multi-step or typing). */
export const FILTER_TYPES_STAY_OPEN_AFTER_APPLY = ['text', 'quiz', 'roles', 'statuses', 'date-range'];

export function shouldCloseFilterDropdownAfterApply(filter) {
    if (!filter?.type) {
        return false;
    }

    return !FILTER_TYPES_STAY_OPEN_AFTER_APPLY.includes(filter.type);
}

// eslint-disable-next-line import/prefer-default-export
export function useFilterManager(filters, emit, options = {}) {
    // Reactive state for form fields
    const formFields = reactive({});

    // Reactive state for expanded sections
    const expandedSections = reactive({});

    // Data for complex filter types
    const filterData = reactive({
        allRoles: [],
        allStatuses: [],
        activityTypes: [],
        localSelectedRoleIds: [],
        localSelectedStatusNames: [],
    });

    const hasExistingFieldValue = (filter, existingValue) => {
        if (filter.type === 'date-range') {
            return (
                existingValue &&
                existingValue.dates &&
                Array.isArray(existingValue.dates) &&
                existingValue.dates.length > 0
            );
        }
        if (filter.type === 'min-max') {
            return existingValue && (existingValue.min || existingValue.max);
        }
        return existingValue !== undefined && existingValue !== null && existingValue !== '';
    };

    const initializeFieldValue = filter => {
        if (filter.type === 'text' || filter.type === 'quiz') {
            formFields[filter.field] = '';
        } else if (filter.type === 'min-max') {
            formFields[filter.field] = { min: '', max: '' };
        } else if (filter.type === 'date-range') {
            formFields[filter.field] = { dates: null };
        }
    };

    const initializeFields = () => {
        filters.forEach(filter => {
            const existingValue = formFields[filter.field];
            const hasExistingValue = hasExistingFieldValue(filter, existingValue);

            if (!hasExistingValue) {
                initializeFieldValue(filter);
            }

            // Initialize expanded state
            expandedSections[filter.field] = filter.is_applied || filter.startExpanded || false;
        });
    };

    // Computed property for applied filters count
    const appliedFiltersCount = computed(() => {
        return filters.filter(filter => {
            // Read refs so Vue tracks modal filters (Course/ActivityType) when QueryBuilder was reset
            [filter.value, filter.selectedTitle];

            return filter.is_applied;
        }).length;
    });

    // Computed properties for role filtering
    const availableRoles = computed(() => {
        const selectedRoleIds = filterData.localSelectedRoleIds;
        const normalizedSelectedIds = selectedRoleIds.map(id => parseInt(id, 10));

        return filterData.allRoles.filter(role => {
            const roleIdInt = parseInt(role.id, 10);
            return !normalizedSelectedIds.includes(roleIdInt);
        });
    });

    // Computed properties for status filtering
    const availableStatuses = computed(() => {
        const selectedStatusNames = filterData.localSelectedStatusNames;
        return filterData.allStatuses.filter(status => {
            return !selectedStatusNames.includes(status.name);
        });
    });

    // Generic apply filter method with debouncing
    const runAfterApply = filter => {
        if (shouldCloseFilterDropdownAfterApply(filter)) {
            options.afterApplyFilter?.(filter);
        }
    };

    const createDebouncedApplyFilter = (delay = 500) => {
        const debounced = debounce((filter, value, event = null) => {
            const filterEvent = filter.captureEvent({ event, value });
            emit('apply-filter', filterEvent);
        }, delay);

        return (filter, value, event = null) => {
            if ((filter.type === 'text' || filter.type === 'quiz') && filter.field in formFields) {
                formFields[filter.field] = value ?? '';
            }

            runAfterApply(filter);

            // Only debounce typing filters. Selections (course, activity type, roles, etc.) apply immediately.
            const isTypingFilter = filter.type === 'text' || filter.type === 'quiz';
            if (isTypingFilter) {
                debounced(filter, value, event);
            } else {
                const filterEvent = filter.captureEvent({ event, value });
                emit('apply-filter', filterEvent);
            }
        };
    };

    const applyFilterImmediate = (filter, value, event = null) => {
        const eventData = filter.type === 'date-range' ? { event, dates: value } : { event, value };

        const filterEvent = filter.captureEvent(eventData);
        emit('apply-filter', filterEvent);
        runAfterApply(filter);
    };

    const debouncedApplyFilter = createDebouncedApplyFilter();

    // Track user-collapsed sections to prevent auto-expansion
    const userCollapsedSections = reactive(new Set());

    // Section toggle functionality
    const toggleSection = field => {
        try {
            const wasExpanded = expandedSections[field];
            expandedSections[field] = !expandedSections[field];

            // Track user intention: if they collapsed it, remember that
            if (wasExpanded && !expandedSections[field]) {
                userCollapsedSections.add(field);
            } else if (!wasExpanded && expandedSections[field]) {
                // If they expanded it, remove from collapsed tracking
                userCollapsedSections.delete(field);
            }
        } catch (error) {
            console.error('Error updating expandedSections:', error);
            // Fallback approach
            const newExpanded = { ...expandedSections };
            const wasExpanded = expandedSections[field];
            newExpanded[field] = !expandedSections[field];
            Object.assign(expandedSections, newExpanded);

            // Track user intention in fallback too
            if (wasExpanded && !newExpanded[field]) {
                userCollapsedSections.add(field);
            } else if (!wasExpanded && newExpanded[field]) {
                userCollapsedSections.delete(field);
            }
        }
    };

    // Clear all filters
    const clearFilters = () => {
        emit('clear-filters');

        // Reset form fields
        Object.keys(formFields).forEach(key => {
            const filter = filters.find(filterItem => filterItem.field === key);
            if (filter) {
                if (filter.type === 'text' || filter.type === 'quiz') {
                    formFields[key] = '';
                } else if (filter.type === 'min-max') {
                    formFields[key] = { min: '', max: '' };
                } else if (filter.type === 'date-range') {
                    formFields[key] = { dates: null };
                }
            }
        });

        // Reset local selections
        filterData.localSelectedRoleIds = [];
        filterData.localSelectedStatusNames = [];
    };

    // Fetch external data for complex filters
    const fetchFilterData = async () => {
        // Fetch roles if needed
        if (filters.some(filter => filter.type === 'roles')) {
            try {
                const { data } = await axios.get('/api/roles');
                const rolesArray = Array.isArray(data.data) ? data.data : Object.values(data.data);

                // Sort roles alphabetically by name
                const sortedRoles = rolesArray.sort((a, b) => a.name.localeCompare(b.name));

                filterData.allRoles = sortedRoles;
            } catch (error) {
                console.error('Failed to fetch roles:', error);
            }
        }

        // Fetch statuses if needed
        if (filters.some(filter => filter.type === 'statuses')) {
            try {
                const { data } = await axios.get('/api/statuses?model=user');
                filterData.allStatuses = Array.isArray(data.data) ? data.data : Object.values(data.data || data);
            } catch (error) {
                console.error('Failed to fetch available statuses:', error);
            }
        }
    };

    // Role-specific methods
    const addRoleToFilter = (roleId, filter) => {
        const currentValues = filterData.localSelectedRoleIds;
        const newValues = currentValues.includes(roleId) ? currentValues : [...currentValues, roleId];

        filterData.localSelectedRoleIds = newValues;
        const roleIds = newValues.length > 0 ? newValues : null;
        debouncedApplyFilter(filter, roleIds);
    };

    const removeRoleFromFilter = (roleId, filter) => {
        const newValues = filterData.localSelectedRoleIds.filter(id => id !== roleId);
        filterData.localSelectedRoleIds = newValues;
        const roleIds = newValues.length > 0 ? newValues : null;
        debouncedApplyFilter(filter, roleIds);
    };

    const getRoleName = roleId => {
        const role = filterData.allRoles.find(roleItem => roleItem.id === roleId);
        return role ? role.name : 'Unknown Role';
    };

    // Status-specific methods
    const addStatusToFilter = (statusName, filter) => {
        const currentValues = filterData.localSelectedStatusNames;
        const newValues = currentValues.includes(statusName) ? currentValues : [...currentValues, statusName];

        filterData.localSelectedStatusNames = newValues;
        debouncedApplyFilter(filter, newValues);
    };

    const removeStatusFromFilter = (statusName, filter) => {
        const newValues = filterData.localSelectedStatusNames.filter(name => name !== statusName);
        filterData.localSelectedStatusNames = newValues;
        const statusNames = newValues.length > 0 ? newValues : null;
        debouncedApplyFilter(filter, statusNames);
    };

    // Animation helpers for transitions
    const onEnter = el => {
        el.style.height = '0';
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight;
        el.style.height = `${el.scrollHeight}px`;
    };

    const onLeave = el => {
        el.style.height = `${el.scrollHeight}px`;
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight;
        el.style.height = '0';
    };

    // Helper functions for syncing different filter types
    const syncRoleFilter = filter => {
        if (filter.type === 'roles' && filter.value) {
            filterData.localSelectedRoleIds = Array.isArray(filter.value) ? filter.value : [];
        }
    };

    const syncStatusFilter = filter => {
        if (filter.type === 'statuses' && filter.value) {
            filterData.localSelectedStatusNames = Array.isArray(filter.value) ? filter.value : [];
        }
    };

    const syncDateRangeFilter = filter => {
        if (filter.type === 'date-range' && filter.value) {
            formFields[filter.field] = { dates: filter.value };
        } else if (filter.type === 'date-range' && !filter.value) {
            formFields[filter.field] = { dates: null };
        } else if (
            filter.type !== 'date-range' &&
            filter.field &&
            (filter.field.includes('login') || filter.field.includes('created'))
        ) {
        }
    };

    const syncFilterToUI = filter => {
        syncRoleFilter(filter);
        syncStatusFilter(filter);
        syncDateRangeFilter(filter);

        // Only auto-expand if the user hasn't explicitly collapsed this section
        if ((filter.is_applied || filter.startExpanded) && !userCollapsedSections.has(filter.field)) {
            expandedSections[filter.field] = true;
        }
    };

    watch(
        () => filters,
        newFilters => {
            if (Array.isArray(newFilters)) {
                newFilters.forEach(syncFilterToUI);
            }
        },
        { deep: true, immediate: true }
    );

    // Initialize everything on mount
    onMounted(() => {
        initializeFields();
        fetchFilterData();
    });

    return {
        // State
        formFields,
        expandedSections,
        filterData,

        // Computed
        appliedFiltersCount,
        availableRoles,
        availableStatuses,

        // Methods
        debouncedApplyFilter,
        applyFilterImmediate,
        toggleSection,
        clearFilters,
        addRoleToFilter,
        removeRoleFromFilter,
        getRoleName,
        addStatusToFilter,
        removeStatusFromFilter,
        onEnter,
        onLeave,
        initializeFields,
        fetchFilterData,
    };
}
